import { AdgroupFormData, CampaignFormData, IOS14AboveId, IOS14BaseId, NO_LIMIT, PlatformType } from '@tt4b/ttam-enums';
import { NetStateEnum, useAdgroupIos14, useStore } from '@tt4b/ttam-hooks';
import { computed, Ref } from '@vue/composition-api';
import { usePlatformIs } from './../../../hooks/creation/adgroup/usePlatformIs/index';
import { i18n } from './../../../i18n';
import { stringCompare } from '@tt4b/ttam-utils';

let preOpSysFilter;

export function useDataSource(opt: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { opSysFilter } = useAdgroupIos14({
    campaignFormData: opt.campaignData,
    adgroupFormData: opt.adgroupData,
  });
  const { getStore } = useStore();
  const { data: opSystemList, run } = getStore(NetStateEnum.osvList, opSysFilter.value);

  function loadData() {
    // 判断一下是否需要重新请求，目前条件只有 opSysFilter，变了就重新拿数据
    if (opSysFilter.value === preOpSysFilter && opSystemList?.value) {
      return opSystemList.value;
    } else {
      preOpSysFilter = opSysFilter.value;
      return run(opSysFilter.value);
    }
  }

  return {
    opSystemList,
    loadData,
  };
}

export function useOsvOptions(opt: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { state } = useStore();
  const opSystemList = computed(() => state.osvList.data.value);
  const defaultOSOption = [
    {
      i18n_value: i18n.t('list_no_limit') as string,
      id: 0,
      fakeId: NO_LIMIT,
    },
  ];
  const { isIOS14Visible, isIOS14Above } = useAdgroupIos14({
    campaignFormData: opt.campaignData,
    adgroupFormData: opt.adgroupData,
  });
  const { iOSPlatformDisabled } = usePlatformIs({
    adgroupData: opt.adgroupData,
    campaignData: opt.campaignData,
  });
  const osVersionOptions = computed(() => {
    const options = (() => {
      // 部分可需要的联动逻辑
      if (!opSystemList?.value) {
        return defaultOSOption;
      }
      const _platform = opt.adgroupData.value.platform;
      const allOptions = opSystemList.value || { android: [], ios: [] };
      const { android = [], ios: iOS = [] } = allOptions;
      if (_platform === PlatformType.ANDROID) {
        return defaultOSOption.concat(android);
      }
      if (_platform === PlatformType.IOS) {
        if (isIOS14Above.value) {
          return iOS.filter(item => item.id > IOS14BaseId);
        }
        if (isIOS14Visible.value) {
          return iOS;
        }
        return [...iOS];
      }
      if (iOSPlatformDisabled.value) {
        return defaultOSOption.concat(android);
      }
      return defaultOSOption.concat(android).concat(iOS);
    })();
    return options.sort((a, b) => {
      // 保证All始终在第一位
      if (a.fakeId === NO_LIMIT) {
        return -1;
      }
      // 保证All始终在第一位
      if (b.fakeId === NO_LIMIT) {
        return 1;
      }

      return stringCompare(b.i18n_value, a.i18n_value, i18n.locale);
    });
  });

  return {
    osVersionOptions,
  };
}

export function useOsvName(options: { campaignData: Ref<CampaignFormData>; adgroupData: Ref<AdgroupFormData> }) {
  const { campaignData, adgroupData } = options;
  const { osVersionOptions } = useOsvOptions({
    campaignData,
    adgroupData,
  });
  const osvName = computed(
    () => osVersionOptions.value.find(e => e.fakeId === adgroupData.value.osVersionFakeId)?.i18n_value,
  );

  return {
    osvName,
  };
}

export function useInitDataGet(opt: { campaignData: Ref<CampaignFormData>; adgroupData: Ref<AdgroupFormData> }) {
  const { campaignData, adgroupData } = opt;
  const { isIOS14Above } = useAdgroupIos14({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  return {
    initializeOsVersion: computed(() => {
      if (isIOS14Above.value) {
        return `${PlatformType.IOS}@${IOS14AboveId}`;
      } else {
        return NO_LIMIT;
      }
    }),
  };
}
